import { useState, useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  ChevronUp,
  Menu,
  X,
  Check,
  Calendar,
  ChevronLeft,
} from "lucide-react";
const rebelLogo = "/assets/d85ba7a4098672812ab5e992f2ac839653942275.png";
const labImage1 = "/assets/f9564ab801a1786dc9a7f34bcbbceeaa941ff959.png";
const labImage2 = "/assets/4f21c0609349d1bcd214c081325ab2edbe7a6597.png";
const labImage3 = "/assets/b93eda330bcd12479a4b8aeeca5186042c9f7384.png";
interface StartupPageProps {
  onNavigateBack: () => void;
  onNavigateToCursoOnline: () => void;
  onNavigateToCursoAvanzado: () => void;
  onNavigateToPresencial: () => void;
}

export function StartupPage({ onNavigateBack, onNavigateToCursoOnline, onNavigateToCursoAvanzado, onNavigateToPresencial }: StartupPageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showNavbar, setShowNavbar] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const backgroundY = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", "100%"],
  );
  const textY = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", "200%"],
  );

  // Control scroll to top button and navbar visibility
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setShowScrollTop(scrollY > 500);
      setShowNavbar(scrollY > 500);
    };

    window.addEventListener("scroll", handleScroll);
    return () =>
      window.removeEventListener("scroll", handleScroll);
  }, []);

  // Smooth scroll to section
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      setMobileMenuOpen(false);
    }
  };

  // Scroll to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle form input changes
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement
    >,
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const navItems = [
    { id: "inicio", label: "Inicio" },
    { id: "cursos", label: "Cursos" },
    { id: "comparativa", label: "Comparativa" },
    { id: "profesor", label: "Profesor" },
    { id: "calendario", label: "Calendario" },
    { id: "contacto", label: "Contacto" },
  ];

  return (
    <div ref={containerRef} className="relative">
      {/* Navigation Bar */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{
          y: showNavbar ? 0 : -100,
          opacity: showNavbar ? 1 : 0,
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-sm border-b border-orange-500/30"
        style={{ pointerEvents: showNavbar ? "auto" : "none" }}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={onNavigateBack}
                className="text-white hover:text-orange-500 mr-4"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Volver
              </Button>
              <div
                className="flex items-center cursor-pointer"
                onClick={() => scrollToSection("inicio")}
              >
                <img
                  src={rebelLogo}
                  alt="Rebel Genomics"
                  className="w-8 h-8 mr-2"
                />
                <span className="text-white text-lg">
                  RebelGenomics
                </span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="text-white hover:text-orange-500 transition-colors duration-200 text-sm uppercase tracking-wide"
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X size={24} />
              ) : (
                <Menu size={24} />
              )}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="md:hidden bg-black/95 border-t border-orange-500/30"
            >
              <div className="py-4 space-y-4">
                {navItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="block w-full text-left px-4 py-2 text-white hover:text-orange-500 transition-colors duration-200 text-sm uppercase tracking-wide"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </motion.nav>

      {/* Scroll to Top Button */}
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{
          opacity: showScrollTop ? 1 : 0,
          scale: showScrollTop ? 1 : 0.8,
        }}
        transition={{ duration: 0.3 }}
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 z-50 bg-orange-500 hover:bg-orange-600 text-white p-3 rounded-full shadow-lg transition-colors duration-200"
        style={{
          pointerEvents: showScrollTop ? "auto" : "none",
        }}
      >
        <ChevronUp size={24} />
      </motion.button>

      {/* Hero Section - Bloque 1 */}
      <section
        id="inicio"
        className="relative h-screen overflow-hidden bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 flex items-center justify-center pt-16"
      >
        <motion.div
          style={{ y: backgroundY }}
          className="absolute inset-0 z-0"
        >
          <ImageWithFallback
            src="https://images.unsplash.com/photo-1611166498484-5585e08d5656?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzbWFsbCUyMGJ1c2luZXNzJTIwc3RhcnR1cCUyMGVudHJlcHJlbmV1ciUyMHBvcnRyYWl0fGVufDF8fHx8MTc1ODAwOTExMHww&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
            alt="Small Business Startup Environment"
            className="w-full h-full object-cover opacity-30"
          />
        </motion.div>

        <motion.div
          style={{ y: textY }}
          className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto"
        >
          <motion.img
            src={rebelLogo}
            alt="Rebel Genomics"
            className="w-32 h-32 mx-auto mb-8"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1 }}
          />

          <motion.h1
            className="text-5xl md:text-8xl mb-8 tracking-tight leading-tight"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            ESCALA TU CULTIVO
            <br />
            <span className="text-black">CON CONTROL SANITARIO</span>
            <br />
            <span className="text-3xl md:text-5xl text-yellow-200">
              DESDE EL PRIMER DÍA
            </span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl mb-12 max-w-4xl mx-auto leading-relaxed"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6 }}
          >
            Convierte un espacio pequeño en un minilab eficiente con protocolos claros, 
            trazabilidad y soporte técnico para crecer sin sorpresas.
          </motion.p>

          {/* Micro-bullets */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="max-w-4xl mx-auto mb-12"
          >
            <div className="bg-black/20 backdrop-blur-sm rounded-2xl p-8 border-2 border-white/30">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-white text-lg leading-relaxed">
                    Reduce dependencia de proveedores externos con tu propio banco in vitro.
                  </span>
                </div>
                <div className="flex items-start">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-white text-lg leading-relaxed">
                    Protocolos claros y replicables que facilitan crecer a estándares GACP.
                  </span>
                </div>
                <div className="flex items-start">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-white text-lg leading-relaxed">
                    Multiplicación y conservación en poco espacio, con bajo coste inicial.
                  </span>
                </div>
                <div className="flex items-start">
                  <div className="w-3 h-3 bg-orange-500 rounded-full mt-2 mr-4 flex-shrink-0"></div>
                  <span className="text-white text-lg leading-relaxed">
                    Escalabilidad por fases: empieza pequeño y crece cuando lo necesites.
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2 }}
          >
            <Button
              size="lg"
              className="bg-orange-500 hover:bg-orange-600 text-white text-xl px-16 py-6 rounded-full"
              onClick={() => scrollToSection("cursos")}
            >
              DESCUBRE CÓMO ESCALAR TU OPERACIÓN
            </Button>
          </motion.div>
        </motion.div>
      </section>

      {/* Grid de Cursos - Bloque 2 */}
      <section
        id="cursos"
        className="min-h-screen bg-white text-black flex items-center py-20"
      >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-7xl mb-8 text-orange-500">
              ELIGE EL CURSO
              <br />
              <span className="text-black">
                QUE MEJOR SE ADAPTA A TU EQUIPO
              </span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Curso Online Iniciación */}
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <Card className="bg-gray-50 border-orange-500/20 p-8 rounded-3xl shadow-lg h-full hover:shadow-xl transition-shadow duration-300">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1582719508461-905c673771fd?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxsYWJvcmF0b3J5JTIwc3RlcmlsZSUyMHRlY2huaXF1ZSUyMGFzZXB0aWN8ZW58MXx8fHwxNzU4MDEzMzU0fDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="Small Business Laboratory Setup"
                  className="w-full h-48 object-cover rounded-2xl mb-6"
                />
                <h3 className="text-2xl mb-4 text-orange-500 uppercase font-bold text-center">
                  CURSO ONLINE INICIACIÓN
                </h3>
                <h4 className="text-lg mb-4 text-gray-800 font-semibold">
                  "Arranca sin inversión grande"
                </h4>
                <div className="space-y-3 mb-8">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Introducción práctica al cultivo in vitro.
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Aprende técnica aséptica y cultivo nodal.
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Establece un primer banco de germoplasma.
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      SOPs y glosario que tu equipo puede aplicar.
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Clases en directo desde el laboratorio.
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Grabaciones accesibles por 90 días.
                    </span>
                  </div>
                </div>
                <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white text-lg py-3 rounded-full" onClick={onNavigateToCursoOnline}>
                  VER CURSO
                </Button>
              </Card>
            </motion.div>

            {/* Curso Online Avanzado */}
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Card className="bg-gray-50 border-orange-500/20 p-8 rounded-3xl shadow-lg h-full hover:shadow-xl transition-shadow duration-300">
                <ImageWithFallback
                  src="https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxsYWJvcmF0b3J5JTIwbWVkaWElMjBwcmVwYXJhdGlvbiUyMGZpdG9ob3Jtb25lc3xlbnwxfHx8fDE3NTgwMTMzNTh8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="Advanced Production Setup"
                  className="w-full h-48 object-cover rounded-2xl mb-6"
                />
                <h3 className="text-2xl mb-4 text-orange-500 uppercase font-bold text-center">
                  CURSO ONLINE AVANZADO
                </h3>
                <h4 className="text-lg mb-4 text-gray-800 font-semibold">
                  "Optimiza y controla tu producción"
                </h4>
                <div className="space-y-3 mb-8">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Preparación de medios adaptados a tu genética.
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Uso de fitohormonas y aditivos explicados paso a paso.
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Termoterapia y Generación Cero (G0).
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Plan de trabajo para montar tu minilab.
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Protocolos de detección de patógenos y control sanitario.
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Multiplicación trazable a escala pequeña o mediana.
                    </span>
                  </div>
                </div>
                <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white text-lg py-3 rounded-full" onClick={onNavigateToCursoAvanzado}>
                  VER CURSO
                </Button>
              </Card>
            </motion.div>

            {/* Masterclass Presencial */}
            <motion.div
              initial={{ opacity: 0, y: 100 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <Card className="bg-gray-50 border-orange-500/20 p-8 rounded-3xl shadow-lg h-full hover:shadow-xl transition-shadow duration-300">
                <img
                  src={labImage1}
                  alt="Masterclass Presencial RebelGenomics"
                  className="w-full h-48 object-cover rounded-2xl mb-6"
                />
                <h3 className="text-2xl mb-4 text-orange-500 uppercase font-bold text-center">
                  MASTERCLASS PRESENCIAL
                </h3>
                <h4 className="text-lg mb-4 text-gray-800 font-semibold">
                  "Crecimiento acelerado con experiencia real"
                </h4>
                <div className="space-y-3 mb-8">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Un día intensivo en laboratorio real.
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Trabaja con cabina de flujo, autoclave y medios estériles.
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Aprende técnicas de meristemos y semillas sintéticas.
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Diseña tu propio flujo de cuarentena y minilab.
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Casos prácticos para resolver problemas comunes de startups.
                    </span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-orange-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                    <span className="text-gray-700">
                      Incluye manual completo y soporte post-curso.
                    </span>
                  </div>
                </div>
                <Button className="w-full bg-orange-500 hover:bg-orange-600 text-white text-lg py-3 rounded-full" onClick={onNavigateToPresencial}>
                  VER CURSO
                </Button>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tabla Comparativa - Bloque 3 */}
      <section
        id="comparativa"
        className="min-h-screen bg-gradient-to-br from-gray-100 to-white flex items-center py-20"
      >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-5xl md:text-7xl mb-8 text-orange-500">
              COMPARA
              <br />
              <span className="text-black">
                NUESTROS CURSOS
              </span>
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white rounded-3xl shadow-2xl overflow-hidden"
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-orange-500 text-white">
                    <th className="px-6 py-4 text-left text-lg font-bold">
                      Características
                    </th>
                    <th className="px-6 py-4 text-center text-lg font-bold">
                      Online Iniciación
                    </th>
                    <th className="px-6 py-4 text-center text-lg font-bold">
                      Online Avanzado
                    </th>
                    <th className="px-6 py-4 text-center text-lg font-bold">
                      Masterclass Presencial
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold">
                      Nivel
                    </td>
                    <td className="px-6 py-4 text-center">
                      Iniciación
                    </td>
                    <td className="px-6 py-4 text-center">
                      Intermedio
                    </td>
                    <td className="px-6 py-4 text-center">
                      Iniciación / Intermedio
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="px-6 py-4 font-semibold">
                      Duración
                    </td>
                    <td className="px-6 py-4 text-center">
                      2×2,5 h (5 h totales)
                    </td>
                    <td className="px-6 py-4 text-center">
                      2×2,5 h (5 h totales)
                    </td>
                    <td className="px-6 py-4 text-center">
                      7 h en un día
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold">
                      Formato
                    </td>
                    <td className="px-6 py-4 text-center">
                      Online en directo
                    </td>
                    <td className="px-6 py-4 text-center">
                      Online en directo
                    </td>
                    <td className="px-6 py-4 text-center">
                      Presencial en Madrid
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="px-6 py-4 font-semibold">
                      Grupo
                    </td>
                    <td className="px-6 py-4 text-center">
                      Máx. 15 personas
                    </td>
                    <td className="px-6 py-4 text-center">
                      Máx. 15 personas
                    </td>
                    <td className="px-6 py-4 text-center">
                      Máx. 4 personas
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold">
                      Incluye prácticas
                    </td>
                    <td className="px-6 py-4 text-center">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                      <div className="text-sm text-gray-600 mt-1">
                        en laboratorio real
                      </div>
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="px-6 py-4 font-semibold">
                      Ideal para...
                    </td>
                    <td className="px-6 py-4 text-center text-sm">
                      Primer paso sin experiencia previa,
                      inversión mínima
                    </td>
                    <td className="px-6 py-4 text-center text-sm">
                      Mejorar medios, usar fitohormonas,
                      diagnosticar patógenos
                    </td>
                    <td className="px-6 py-4 text-center text-sm">
                      Implementar rápido, visión completa,
                      experiencia práctica
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold">
                      Documentación
                    </td>
                    <td className="px-6 py-4 text-center text-sm">
                      Guía 10 páginas, glosario, SOP básicos
                    </td>
                    <td className="px-6 py-4 text-center text-sm">
                      Guía 10 páginas, protocolos avanzados
                    </td>
                    <td className="px-6 py-4 text-center text-sm">
                      Guía 35 páginas, protocolos completos
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="px-6 py-4 font-semibold">
                      Introducción al cultivo in vitro
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold">
                      Seguridad y laboratorio
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="px-6 py-4 font-semibold">
                      Técnica estéril y nodal
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold">
                      Meristemos
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="px-6 py-4 font-semibold">
                      Generación Cero
                    </td>
                    <td className="px-6 py-4 text-center">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold">
                      Preparación de medios & fitohormonas
                    </td>
                    <td className="px-6 py-4 text-center">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="px-6 py-4 font-semibold">
                      Semillas sintéticas
                    </td>
                    <td className="px-6 py-4 text-center">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold">
                      Aclimatación
                    </td>
                    <td className="px-6 py-4 text-center">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="px-6 py-4 font-semibold">
                      Termoterapia
                    </td>
                    <td className="px-6 py-4 text-center">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold">
                      Test de patógenos
                    </td>
                    <td className="px-6 py-4 text-center">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <X className="w-6 h-6 text-red-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="px-6 py-4 font-semibold">
                      Soporte post-curso
                    </td>
                    <td className="px-6 py-4 text-center text-sm">
                      Grupo privado
                    </td>
                    <td className="px-6 py-4 text-center text-sm">
                      Grupo privado
                    </td>
                    <td className="px-6 py-4 text-center text-sm">
                      2h personalizadas + grupo privado
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold">
                      Protocolos y documentación descargable
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="px-6 py-4 font-semibold">
                      Certificado de asistencia
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="px-6 py-4 font-semibold">
                      Grabación de la clase descargable
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Check className="w-6 h-6 text-green-500 mx-auto" />
                    </td>
                  </tr>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="px-6 py-4 font-semibold text-lg">
                      Precio
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="text-2xl font-bold text-orange-500">
                        299 €
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="text-2xl font-bold text-orange-500">
                        299 €
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="text-2xl font-bold text-orange-500">
                        1.499 €
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-6 py-8"></td>
                    <td className="px-6 py-8 text-center">
                      <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-full text-lg font-bold uppercase tracking-wide">
                        VER CURSO
                      </Button>
                    </td>
                    <td className="px-6 py-8 text-center">
                      <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-full text-lg font-bold uppercase tracking-wide">
                        VER CURSO
                      </Button>
                    </td>
                    <td className="px-6 py-8 text-center">
                      <Button className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-full text-lg font-bold uppercase tracking-wide">
                        VER CURSO
                      </Button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Sobre el Profesor - Bloque 4 */}
      <section
        id="profesor"
        className="min-h-screen bg-black text-white flex items-center py-20"
      >
        <div className="container mx-auto px-4">
          {/* Primera fila: Foto y resumen 50% cada uno */}
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMG1hbiUyMHNjaWVudGlzdHxlbnwxfHx8fDE3NTgwMDk0MjV8MA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                alt="Pedro Cifuentes"
                className="w-full h-full object-cover rounded-2xl"
                style={{ height: "100%" }}
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <h2 className="text-5xl md:text-7xl text-orange-500">
                SOBRE EL
                <br />
                <span className="text-white">PROFESOR</span>
              </h2>

              <div className="bg-orange-500/10 p-8 rounded-2xl border border-orange-500/30">
                <h3 className="text-3xl mb-4 text-orange-400">
                  Pedro Cifuentes
                </h3>
                <p className="text-xl text-gray-300 leading-relaxed">
                  Pedro Cifuentes lleva más de siete años
                  especializándose en plantas medicinales y
                  biotecnología aplicada. Ha colaborado con más
                  de 40 universidades y centros de investigación
                  españoles de referencia, incluyendo Cicytex,
                  CRAG, CSIC-CEBAS y CBGP.
                  <br />
                  Formado inicialmente en Plant Cell Technology
                  con Bill Graham, ahora colabora como miembro
                  de PCT Evangelist, Pedro se ha dedicado a
                  llevar el cultivo in vitro del laboratorio a
                  la práctica real. Ha trabajado en proyectos de
                  innovación hortícola con Bioled Lighting y ha
                  aplicado estas técnicas en situaciones reales
                  de producción.
                  <br />
                  En los últimos años ha formado a más de 100
                  cultivadores y técnicos en metodologías
                  avanzadas de conservación, saneamiento y
                  multiplicación de genéticas. Su enfoque
                  combina rigor científico con explicaciones
                  claras y aplicaciones prácticas que los
                  alumnos pueden implementar desde el primer
                  día.
                  <br />
                  Como divulgador, mantiene una comunidad de más
                  de 2.700 suscriptores en YouTube y 14.000
                  seguidores en Instagram, donde comparte
                  conocimiento técnico de forma accesible. Pedro
                  se caracteriza por su capacidad para
                  simplificar conceptos complejos y resolver
                  dudas de manera directa.
                  <br />
                  Su objetivo es formar profesionales que no
                  solo comprendan el cultivo in vitro, sino que
                  sepan aplicarlo con éxito para obtener
                  resultados tangibles en sus proyectos.
                </p>
              </div>
            </motion.div>
          </div>

          {/* Segunda fila: 3 banners centrados */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="max-w-6xl mx-auto"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-r from-orange-500 to-red-500 p-8 rounded-2xl border-2 border-orange-400 relative overflow-hidden cursor-pointer group hover:scale-105 transition-transform duration-300">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-white/20 p-4 rounded-full">
                      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 2L3 7v11h4v-6h6v6h4V7l-7-5z"/>
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-white text-center text-2xl mb-2 uppercase tracking-wide">
                    VIDEOS GRATUITOS
                  </h3>
                  <p className="text-white/90 text-center text-lg">
                    Mira nuestros tutoriales
                  </p>
                  <p className="text-white/90 text-center">
                    en YouTube
                  </p>
                  <div className="mt-4 text-center">
                    <span className="inline-block bg-white/20 px-4 py-2 rounded-full text-white text-sm uppercase tracking-wider">
                      Ver Canal
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-gradient-to-r from-orange-500 to-red-500 p-8 rounded-2xl border-2 border-orange-400 relative overflow-hidden cursor-pointer group hover:scale-105 transition-transform duration-300">
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-white/20 p-4 rounded-full">
                      <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                      </svg>
                    </div>
                  </div>
                  <h3 className="text-white text-center text-2xl mb-2 uppercase tracking-wide">
                    PERFIL LINKEDIN
                  </h3>
                  <p className="text-white/90 text-center text-lg">
                    Conecta con Pedro
                  </p>
                  <p className="text-white/90 text-center">
                    profesionalmente
                  </p>
                  <div className="mt-4 text-center">
                    <span className="inline-block bg-white/20 px-4 py-2 rounded-full text-white text-sm uppercase tracking-wider">
                      Ver Perfil
                    </span>
                  </div>
                </div>
              </div>
              <div
                className="bg-gradient-to-r from-orange-500 to-red-500 p-8 rounded-2xl border-2 border-orange-400 relative overflow-hidden cursor-pointer group hover:scale-105 transition-transform duration-300"
                onClick={() =>
                  window.open("https://calendly.com", "_blank")
                }
              >
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-center mb-4">
                    <div className="bg-white/20 p-4 rounded-full">
                      <Calendar className="w-8 h-8 text-white" />
                    </div>
                  </div>
                  <h3 className="text-white text-center text-2xl mb-2 uppercase tracking-wide">
                    ¿HABLAR CONMIGO?
                  </h3>
                  <p className="text-white/90 text-center text-lg">
                    Agenda una reunión
                  </p>
                  <p className="text-white/90 text-center">
                    gratuita de consultoría
                  </p>
                  <div className="mt-4 text-center">
                    <span className="inline-block bg-white/20 px-4 py-2 rounded-full text-white text-sm uppercase tracking-wider">
                      Agendar Reunión
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Calendario y FAQs - Bloque 5 */}
      <section
        id="calendario"
        className="min-h-screen bg-gradient-to-br from-gray-100 to-white flex items-center py-20"
      >
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Calendario */}
            <motion.div
              initial={{ opacity: 0, x: -100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-6xl mb-8 text-orange-500">
                PRÓXIMAS
                <br />
                <span className="text-black">FECHAS</span>
              </h2>

              <div className="space-y-4">
                <Card className="bg-white border-orange-500/20 p-6 rounded-2xl shadow-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center mb-2">
                        <Calendar className="w-5 h-5 text-orange-500 mr-2" />
                        <span className="text-orange-500">
                          11 y 12 diciembre 2025
                        </span>
                      </div>
                      <h3 className="text-xl text-black">
                        Masterclass Online Introducción al
                        cultivo invitro
                      </h3>
                    </div>
                    <Button
                      size="sm"
                      className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-full"
                    >
                      INSCRIBIRME
                    </Button>
                  </div>
                </Card>

                <Card className="bg-white border-orange-500/20 p-6 rounded-2xl shadow-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center mb-2">
                        <Calendar className="w-5 h-5 text-orange-500 mr-2" />
                        <span className="text-orange-500">
                          18 y 19 diciembre 2025
                        </span>
                      </div>
                      <h3 className="text-xl text-black">
                        Masterclass Online Avanzado de cultivo
                        invitro
                      </h3>
                    </div>
                    <Button
                      size="sm"
                      className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-full"
                    >
                      INSCRIBIRME
                    </Button>
                  </div>
                </Card>

                <Card className="bg-white border-orange-500/20 p-6 rounded-2xl shadow-lg">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center mb-2">
                        <Calendar className="w-5 h-5 text-orange-500 mr-2" />
                        <span className="text-orange-500">
                          9 y 10 diciembre 2025
                        </span>
                      </div>
                      <h3 className="text-xl text-black">
                        Masterclass Presencial Cultivo Invitro
                      </h3>
                    </div>
                    <Button
                      size="sm"
                      className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-full"
                    >
                      INSCRIBIRME
                    </Button>
                  </div>
                </Card>
              </div>
            </motion.div>

            {/* FAQs */}
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-6xl mb-8 text-orange-500">
                PREGUNTAS
                <br />
                <span className="text-black">FRECUENTES</span>
              </h2>

              <div className="space-y-4">
                {/* Categoría 1: Organización y logística del curso */}
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                  <Accordion type="single" collapsible>
                    <AccordionItem
                      value="categoria-1"
                      className="border-none"
                    >
                      <AccordionTrigger className="px-6 py-4 text-left text-lg text-orange-500 hover:text-orange-600 hover:no-underline">
                        Organización y logística del curso
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-6">
                        <div className="space-y-4">
                          <div className="border-b border-gray-100 pb-4">
                            <h4 className="text-orange-500 mb-2">
                              ¿Cuál es el horario de los cursos?
                            </h4>
                            <p className="text-black">
                              Las clases online se imparten en
                              directo de 18:00 a 20:30 hora de
                              Madrid, en dos sesiones
                              consecutivas (normalmente jueves y
                              viernes). La masterclass
                              presencial es intensiva: 7 horas
                              de 09:00 a 17:00. Estos horarios
                              son orientativos y cada edición
                              confirmará la agenda exacta.
                            </p>
                          </div>
                          <div className="border-b border-gray-100 pb-4">
                            <h4 className="text-orange-500 mb-2">
                              ¿Cómo se accede a los cursos
                              online?
                            </h4>
                            <p className="text-black">
                              Una vez inscrito recibirás un
                              correo con el enlace privado a la
                              plataforma de streaming (Zoom o
                              similar). Podrás conectarte desde
                              ordenador, tablet o móvil y
                              participar en tiempo real.
                            </p>
                          </div>
                          <div className="border-b border-gray-100 pb-4">
                            <h4 className="text-orange-500 mb-2">
                              ¿Qué pasa si no puedo asistir en
                              la fecha prevista?
                            </h4>
                            <p className="text-black">
                              Puedes mover tu inscripción a otra
                              edición del mismo curso siempre
                              que avises con al menos 48 horas
                              de antelación. De este modo no
                              pierdes tu plaza y puedes
                              reprogramar tu aprendizaje.
                            </p>
                          </div>
                          <div>
                            <h4 className="text-orange-500 mb-2">
                              ¿Cuántas personas pueden
                              participar en la formación?
                            </h4>
                            <p className="text-black">
                              Los cursos online están limitados
                              a 15 personas para garantizar
                              interacción y cercanía. La
                              masterclass presencial es aún más
                              exclusiva: máximo 4 alumnos por
                              edición.
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>

                {/* Categoría 2: Requisitos y nivel de los alumnos */}
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                  <Accordion type="single" collapsible>
                    <AccordionItem
                      value="categoria-2"
                      className="border-none"
                    >
                      <AccordionTrigger className="px-6 py-4 text-left text-lg text-orange-500 hover:text-orange-600 hover:no-underline">
                        Requisitos y nivel de los alumnos
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-6">
                        <div className="space-y-4">
                          <div className="border-b border-gray-100 pb-4">
                            <h4 className="text-orange-500 mb-2">
                              ¿Necesito experiencia previa en
                              cultivo in vitro?
                            </h4>
                            <p className="text-black">
                              Los cursos online de introducción
                              y la masterclass presencial están
                              diseñados para partir desde cero.
                              Para el curso online avanzado es
                              recomendable haber completado el
                              de introducción o contar con una
                              base de biología.
                            </p>
                          </div>
                          <div className="border-b border-gray-100 pb-4">
                            <h4 className="text-orange-500 mb-2">
                              ¿Hay requisitos para inscribirme?
                            </h4>
                            <p className="text-black">
                              No hay requisitos formales para el
                              curso de iniciación ni para la
                              masterclass presencial. Para el
                              avanzado sí recomendamos tener
                              experiencia previa o haber
                              completado el curso básico.
                            </p>
                          </div>
                          <div>
                            <h4 className="text-orange-500 mb-2">
                              ¿Qué equipo necesito para empezar?
                            </h4>
                            <p className="text-black">
                              Para un laboratorio básico basta
                              con una olla a presión (~200 €),
                              una cabina de flujo (~500 €) y
                              consumibles (~100 €). En el curso
                              te enseñamos cómo escalar
                              gradualmente según tu presupuesto.
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>

                {/* Categoría 3: Contenido y metodología */}
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                  <Accordion type="single" collapsible>
                    <AccordionItem
                      value="categoria-3"
                      className="border-none"
                    >
                      <AccordionTrigger className="px-6 py-4 text-left text-lg text-orange-500 hover:text-orange-600 hover:no-underline">
                        Contenido y metodología
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-6">
                        <div className="space-y-4">
                          <div className="border-b border-gray-100 pb-4">
                            <h4 className="text-orange-500 mb-2">
                              ¿Necesito comprar materiales para
                              seguir el curso?
                            </h4>
                            <p className="text-black">
                              No es obligatorio, puedes seguir
                              las demostraciones y tomar nota.
                              Aun así, recibirás una lista
                              opcional de materiales
                              recomendados por si deseas
                              practicar en paralelo.
                            </p>
                          </div>
                          <div className="border-b border-gray-100 pb-4">
                            <h4 className="text-orange-500 mb-2">
                              ¿Las clases quedan grabadas?
                            </h4>
                            <p className="text-black">
                              Sí, tendrás acceso a las
                              grabaciones durante 90 días. Esto
                              te permite repasar contenidos o
                              ponerte al día si no pudiste
                              asistir en directo.
                            </p>
                          </div>
                          <div className="border-b border-gray-100 pb-4">
                            <h4 className="text-orange-500 mb-2">
                              ¿Puedo hacer preguntas durante el
                              curso?
                            </h4>
                            <p className="text-black">
                              Sí, todas las sesiones incluyen
                              espacios de preguntas y respuestas
                              en directo. Además, puedes usar el
                              chat para plantear dudas mientras
                              sigues la clase.
                            </p>
                          </div>
                          <div className="border-b border-gray-100 pb-4">
                            <h4 className="text-orange-500 mb-2">
                              ¿Recibo material del curso?
                            </h4>
                            <p className="text-black">
                              Sí, recibirás presentaciones,
                              manual en PDF con protocolos
                              (SOPs), recetas de medios,
                              glosario y lista de materiales con
                              enlaces a proveedores. Todo queda
                              a tu disposición para aplicar lo
                              aprendido.
                            </p>
                          </div>
                          <div>
                            <h4 className="text-orange-500 mb-2">
                              ¿Qué tipo de plantas se utilizan
                              en las demostraciones?
                            </h4>
                            <p className="text-black">
                              Trabajamos principalmente con
                              cannabis medicinal y, en algunos
                              casos, con ornamentales como
                              monsteras, coleos o alocasias.
                              Esto permite mostrar aplicaciones
                              diversas y comparables.
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>

                {/* Categoría 4: Certificación y soporte */}
                <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                  <Accordion type="single" collapsible>
                    <AccordionItem
                      value="categoria-4"
                      className="border-none"
                    >
                      <AccordionTrigger className="px-6 py-4 text-left text-lg text-orange-500 hover:text-orange-600 hover:no-underline">
                        Certificación y soporte
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-6">
                        <div className="space-y-4">
                          <div className="border-b border-gray-100 pb-4">
                            <h4 className="text-orange-500 mb-2">
                              ¿Se entrega algún diploma?
                            </h4>
                            <p className="text-black">
                              Sí, todos los participantes
                              reciben un certificado digital de
                              asistencia. Este diploma es válido
                              para incluir en tu CV o perfil
                              profesional.
                            </p>
                          </div>
                          <div className="border-b border-gray-100 pb-4">
                            <h4 className="text-orange-500 mb-2">
                              ¿Puedo recibir asesoramiento
                              personalizado para mi laboratorio?
                            </h4>
                            <p className="text-black">
                              Sí, ofrecemos consultorías
                              individuales adaptadas a tus
                              necesidades específicas. Son un
                              servicio adicional para quienes
                              buscan apoyo en diseño de
                              laboratorio, resolución de
                              problemas o validación de
                              protocolos.
                            </p>
                          </div>
                          <div className="border-b border-gray-100 pb-4">
                            <h4 className="text-orange-500 mb-2">
                              ¿Hacéis formaciones individuales o
                              en las instalaciones del cliente?
                            </h4>
                            <p className="text-black">
                              Sí, realizamos formaciones
                              personalizadas tanto online como
                              presenciales en tu laboratorio o
                              empresa. Adaptamos los contenidos
                              a tus necesidades específicas y
                              estudiamos cada propuesta de forma
                              individual.
                            </p>
                          </div>
                          <div>
                            <h4 className="text-orange-500 mb-2">
                              ¿Puedo ver el curso con más
                              personas desde la misma pantalla?
                            </h4>
                            <p className="text-black">
                              No, cada inscripción es individual
                              y da derecho a una única plaza. Si
                              varias personas desean participar
                              deben inscribirse por separado
                              para garantizar la mejor
                              experiencia.
                            </p>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contacto y Lead Magnet - Bloque 6 */}
      <section
        id="contacto"
        className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-orange-900 text-white flex items-center py-20"
      >
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-5xl md:text-7xl mb-8 text-orange-500">
              ¿NO SABES
              <br />
              <span className="text-white">
                QUÉ CURSO ELEGIR?
              </span>
            </h2>

            <p className="text-xl md:text-2xl mb-12 text-gray-300">
              Responde un breve cuestionario y te enviaremos la
              recomendación ideal para ti
            </p>

            <Card className="bg-white/10 backdrop-blur-sm border-orange-500/30 p-8 rounded-3xl max-w-2xl mx-auto">
              <div className="space-y-6">
                <div>
                  <Label
                    htmlFor="name"
                    className="text-white text-lg mb-2 block"
                  >
                    Nombre
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Tu nombre completo"
                    className="bg-white/20 border-white/30 text-white placeholder:text-white/60 rounded-xl"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="email"
                    className="text-white text-lg mb-2 block"
                  >
                    Email
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="tu@email.com"
                    className="bg-white/20 border-white/30 text-white placeholder:text-white/60 rounded-xl"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="phone"
                    className="text-white text-lg mb-2 block"
                  >
                    Teléfono
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="+34 123 456 789"
                    className="bg-white/20 border-white/30 text-white placeholder:text-white/60 rounded-xl"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="message"
                    className="text-white text-lg mb-2 block"
                  >
                    ¿Cuál es tu experiencia actual con el
                    cultivo?
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Cuéntanos sobre tu proyecto, objetivos y experiencia..."
                    className="bg-white/20 border-white/30 text-white placeholder:text-white/60 rounded-xl min-h-[100px]"
                  />
                </div>

                <Button
                  size="lg"
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white text-xl py-6 rounded-full"
                >
                  QUIERO MI RECOMENDACIÓN
                </Button>
              </div>
            </Card>

            {/* Logo at bottom */}
            <motion.div
              className="mt-16"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <img
                src={rebelLogo}
                alt="Rebel Genomics"
                className="w-20 h-20 mx-auto opacity-60"
              />
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}